(() => {
  'use strict';
  const DAY = 86400000;
  const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const weekday = day => ((day + 3) % 7 + 7) % 7;
  const pad = n => String(n).padStart(2, '0');
  const key = d => new Date(d * DAY).toISOString().slice(0, 10);
  const dayOf = s => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw Error('Use YYYY-MM-DD dates.');
    const d = Date.parse(s + 'T00:00:00Z') / DAY;
    if (!Number.isInteger(d) || key(d) !== s) throw Error('Invalid calendar date: ' + s);
    return d;
  };
  function blocked(text) {
    return String(text || '').split(/[\n,]+/).map(x => x.trim()).filter(Boolean).map(line => {
      const pair = line.split('..').map(x => x.trim());
      if (pair.length > 2) throw Error('Separate blocked ranges with ..');
      const annual = /^\d{2}-\d{2}$/.test(pair[0]);
      const end = pair[1] || pair[0];
      if (annual) {
        dayOf('2000-' + pair[0]); dayOf('2000-' + end);
        return d => {
          const md = key(d).slice(5);
          return pair[0] <= end ? md >= pair[0] && md <= end : md >= pair[0] || md <= end;
        };
      }
      const startDay = dayOf(pair[0]), endDay = dayOf(end);
      if (endDay < startDay) throw Error('Blocked range ends before it starts.');
      return d => d >= startDay && d <= endDay;
    });
  }
  function calendar(days, exclusions) {
    const values = String(days).split(',').map(x => Number(x.trim()));
    if (!values.length || values.some(x => !Number.isInteger(x) || x < 1 || x > 7) || !String(days).trim()) throw Error('Working days: use 1–7, Monday–Sunday.');
    const set = new Set(values), rules = blocked(exclusions);
    const valid = d => set.has(weekday(d) + 1) && !rules.some(rule => rule(d));
    const next = d => {
      for (let i = 0; i < 1096; i++, d++) if (valid(d)) return d;
      throw Error('No available working day within three years.');
    };
    return { valid, next, add(d, n) { for (let i = 0; i < n; i++) d = next(d + 1); return d; } };
  }
  function local(now, zone) {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }).formatToParts(now).map(x => [x.type, x.value]));
    return { day: dayOf(`${p.year}-${p.month}-${p.day}`), minutes: +p.hour * 60 + +p.minute, seconds: +p.second };
  }
  function instant(day, minutes, zone) {
    const target = day * DAY + minutes * 60000;
    let result = target;
    for (let i = 0; i < 5; i++) {
      const p = local(new Date(result), zone);
      const delta = target - (p.day * DAY + p.minutes * 60000 + p.seconds * 1000);
      if (!delta) {
        const before = local(new Date(result - 10800000), zone);
        const offsetChange = before.day * DAY + before.minutes * 60000 - (result - 10800000) - (target - result);
        if (offsetChange > 0 && offsetChange <= 10800000) {
          const earlier = local(new Date(result - offsetChange), zone);
          if (earlier.day === day && earlier.minutes === minutes && !earlier.seconds) return result - offsetChange;
        }
        return result;
      }
      result += delta;
    }
    throw Error('Cutoff falls in a DST clock gap. Choose another cutoff time.');
  }
  function count(value) {
    if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) throw Error('Day counts are required. Enter a whole number from 0 to 60.');
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0 || n > 60) throw Error('Day counts must be whole numbers from 0 to 60.');
    return n;
  }
  function dailyCutoffs(c) {
    const parse = (value, label) => {
      const time = String(value ?? '').trim();
      const match = time.match(/^(0?[1-9]|1[0-2])\s*:\s*([0-5]\d)\s*([ap])\s*m$/i);
      if (!match) throw Error(label + ' cutoff needs a 12-hour time with AM or PM, for example 9:30 AM or 4:00 PM.');
      return (Number(match[1]) % 12) * 60 + Number(match[2]) + (match[3].toLowerCase() === 'p' ? 720 : 0);
    };
    return WEEKDAYS.map(name => parse(c['cutoff_' + name], name[0].toUpperCase() + name.slice(1)));
  }
  function cutoffForDay(c, day, schedule = dailyCutoffs(c)) {
    return schedule[weekday(day)];
  }
  function calculate(c, now = new Date()) {
    const pmin = count(c.processing_min), pmax = count(c.processing_max);
    const tmin = count(c.transit_min), tmax = count(c.transit_max);
    if (pmax < pmin || tmax < tmin) throw Error('Maximum days must be at least minimum days.');
    const schedule = dailyCutoffs(c);
    const current = local(now, c.timezone);
    const processing = calendar(c.processing_days, c.processing_blocked);
    const transit = calendar(c.transit_days, c.transit_blocked);
    const todayCutoff = processing.valid(current.day) ? instant(current.day, cutoffForDay(c, current.day, schedule), c.timezone) : null;
    const base = processing.next(current.day + (todayCutoff !== null && now.getTime() >= todayCutoff ? 1 : 0));
    const from = processing.add(base, pmin), to = processing.add(base, pmax);
    const deadline = base === current.day ? todayCutoff : instant(base, cutoffForDay(c, base, schedule), c.timezone);
    return {
      ordered: current.day, processing_from: from, processing_to: to,
      delivery_from: tmin ? transit.add(c.delivery_basis === 'earliest' ? from : to, tmin) : transit.next(c.delivery_basis === 'earliest' ? from : to),
      delivery_to: tmax ? transit.add(to, tmax) : transit.next(to),
      remaining: Math.max(0, deadline - now.getTime())
    };
  }
  function visible(c, market, country) {
    const markets = String(c.markets || '').toLowerCase().split(/[\s,]+/).filter(Boolean);
    const countries = String(c.countries || '').toUpperCase().split(/[\s,]+/).filter(Boolean);
    return (!markets.length && !countries.length) || markets.includes(String(market).toLowerCase()) || countries.includes(String(country).toUpperCase());
  }
  const api = { calculate, visible, dayOf, key, blocked, calendar, instant, local, dailyCutoffs, cutoffForDay };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || customElements.get('ws-delivery')) return;
  class Delivery extends HTMLElement {
    connectedCallback() {
      clearInterval(this.timer);
      this.config = JSON.parse(this.querySelector('[data-ws-config]').textContent);
      this.update();
      this.timer = setInterval(() => { if (!document.hidden) this.update(); }, 30000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    format(d) {
      const c = this.config;
      const date = new Date(d * DAY);
      const opts = { timeZone: 'UTC', day: '2-digit', month: c.date_format === 'long' ? 'long' : 'short' };
      if (c.include_year) opts.year = 'numeric';
      if (c.date_format === 'weekday') opts.weekday = 'short';
      if (c.date_format === 'numeric') {
        const a = [pad(date.getUTCMonth() + 1), pad(date.getUTCDate())];
        if (c.day_first) a.reverse();
        if (c.include_year) a.push(String(date.getUTCFullYear()));
        return a.join(c.date_separator);
      }
      return new Intl.DateTimeFormat(this.dataset.locale || 'en', opts).format(date);
    }
    update() {
      try {
        const c = this.config;
        if (!visible(c, this.dataset.market, this.dataset.country)) { this.hidden = !window.Shopify?.designMode; if (!this.hidden) this.showError('Hidden for this market. Change preview country or visibility settings.'); return; }
        const r = calculate(c);
        const values = {
          ordered_date: this.format(r.ordered), processing_from: this.format(r.processing_from), processing_to: this.format(r.processing_to),
          delivery_from: this.format(r.delivery_from), delivery_to: this.format(r.delivery_to),
          counter: `${pad(Math.floor(r.remaining / 3600000))}h ${pad(Math.floor(r.remaining / 60000) % 60)}min`
        };
        const range = (a, b) => a === b ? this.format(a) : this.format(a) + ' – ' + this.format(b);
        this.querySelector('[data-ws-description]').replaceChildren(...String(c.description).split(/(\{(?:counter|ordered_date|processing_from|processing_to|delivery_from|delivery_to)\})/g).map(part => {
          if (part[0] === '{' && Object.hasOwn(values, part.slice(1, -1))) {
            const strong = document.createElement('strong'); strong.textContent = values[part.slice(1, -1)]; return strong;
          }
          return document.createTextNode(part);
        }));
        this.querySelector('[data-ws-ordered]').textContent = values.ordered_date;
        this.querySelector('[data-ws-processing]').textContent = range(r.processing_from, r.processing_to);
        this.querySelector('[data-ws-delivery]').textContent = range(r.delivery_from, r.delivery_to);
        this.querySelector('[data-ws-error]').hidden = true;
        this.querySelector('[data-ws-body]').hidden = false;
        this.hidden = false;
      } catch (error) { this.showError(error.message); }
    }
    showError(message) {
      this.querySelector('[data-ws-body]').hidden = true;
      this.hidden = !window.Shopify?.designMode;
      const error = this.querySelector('[data-ws-error]');
      error.textContent = 'Delivery estimate: ' + message; error.hidden = false;
    }
  }
  customElements.define('ws-delivery', Delivery);
})();
